import { useState } from "react";
import { CalendarCheck, Check, MoreHorizontal, Phone, Plus, ShieldCheck, TriangleAlert, Users, Mail } from "lucide-react";
import { Avatar } from "@/components/shared/Avatar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { formatCZK, formatCZKCompact } from "@/lib/format";

// 304: Zakázky › Tabule — fáze jako sloupce (podle obrazovky Pipedrive, jen tabule, ne celý styl).
// Karta: název, investor, cena, stavbyvedoucí, další krok (nejbližší otevřený úkol z /projects/next-steps).
// Vedení kartu přetáhne do jiné fáze (PC) nebo ji přesune přes „…“ (i telefon); Přidat založí zakázku ve fázi sloupce.
export const BOARD_STAGES = [
  { key: "Poptávka", label: "Poptávka", statuses: ["Poptávka"] },
  { key: "Nabídka", label: "Nabídka", statuses: ["Nabídka"] },
  { key: "Smlouva podepsána", label: "Smlouva", statuses: ["Smlouva podepsána"] },
  { key: "V realizaci", label: "Realizace", statuses: ["V realizaci"] },
  { key: "Dokončeno", label: "Hotovo", statuses: ["Dokončeno", "Fakturováno", "Záruka"] },
];
export const stageOf = (status) => BOARD_STAGES.find((s) => s.statuses.includes(status)) || BOARD_STAGES[0];

const DOW = ["ne", "po", "út", "st", "čt", "pá", "so"];
const pad = (n) => String(n).padStart(2, "0");
const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const TYPE_ICON = { call: Phone, meeting: Users, email: Mail };
const daysWord = (n) => (n === 1 ? "den" : n >= 2 && n <= 4 ? "dny" : "dní");

function when(deadline, time) {
  if (!deadline) return { text: "bez termínu", late: false };
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const d = new Date(`${deadline.slice(0, 10)}T00:00:00`);
  const t = time ? ` ${time}` : "";
  const iso = deadline.slice(0, 10);
  if (iso < ymd(today)) return { text: `po termínu · ${d.getDate()}. ${d.getMonth() + 1}.`, late: true };
  if (iso === ymd(today)) return { text: `dnes${t}`, late: false };
  const tm = new Date(today); tm.setDate(tm.getDate() + 1);
  if (iso === ymd(tm)) return { text: `zítra${t}`, late: false };
  if (d.getFullYear() !== today.getFullYear()) return { text: `${d.getDate()}. ${d.getMonth() + 1}. ${d.getFullYear()}`, late: false };
  return { text: `${DOW[d.getDay()]} ${d.getDate()}. ${d.getMonth() + 1}.${t}`, late: false };
}

function NextStep({ p, step }) {
  const done = stageOf(p.status).key === "Dokončeno";
  const n = step?.next;
  if (!n && done) {
    return (
      <span className="mt-2 inline-flex items-center gap-1.5 h-7 px-2.5 rounded-[9px] bg-secondary text-muted-foreground text-[12.5px] font-semibold self-start">
        {p.status === "Záruka" ? <ShieldCheck size={14} /> : <Check size={14} />}{p.status}
      </span>
    );
  }
  if (!n) {
    return (
      <span className="mt-2 flex flex-col px-2.5 py-1.5 rounded-[9px] bg-amber-500/15 text-amber-700 dark:text-amber-400 text-[12.5px] leading-snug" data-testid={`board-cold-${p.id}`}>
        <b className="inline-flex items-center gap-1.5 font-semibold"><TriangleAlert size={14} />Chybí další krok</b>
        {step?.idle_days != null && <span className="opacity-80">{step.idle_days} {daysWord(step.idle_days)} bez akce</span>}
      </span>
    );
  }
  const w = when(n.deadline, n.time_from);
  const Icon = TYPE_ICON[n.type] || CalendarCheck;
  return (
    <span className={`mt-2 flex flex-col px-2.5 py-1.5 rounded-[9px] text-[12.5px] leading-snug ${w.late ? "bg-destructive/10 text-destructive" : "bg-success/15 text-success"}`}
      title={`Další krok: ${n.title}`}>
      <b className="flex items-start gap-1.5 font-semibold min-w-0"><Icon size={14} className="shrink-0 mt-[2px]" /><span className="line-clamp-2">{n.title}</span></b>
      <span className="opacity-80">{w.text}</span>
    </span>
  );
}

function Card({ p, step, canEdit, drag, onOpen, onMove, onDragStart, onDragEnd, dragging }) {
  const cold = !step?.next && stageOf(p.status).key !== "Dokončeno" && step !== undefined;
  const team = (p.team || []).length;
  return (
    <article draggable={drag} onDragStart={(e) => onDragStart(e, p)} onDragEnd={onDragEnd}
      onClick={() => onOpen(p)} onKeyDown={(e) => { if (e.key === "Enter") onOpen(p); }} tabIndex={0} role="button"
      aria-label={`${p.name}, fáze ${stageOf(p.status).label}`} data-testid={`board-card-${p.id}`}
      className={`relative flex flex-col bg-card rounded-[14px] border border-border/70 p-3 text-left cursor-pointer outline-none
        shadow-[0_1px_2px_rgba(20,24,33,.06)] hover:shadow-[0_6px_16px_rgba(20,24,33,.10)] focus-visible:ring-2 focus-visible:ring-brand transition-shadow
        ${cold ? "border-l-[3px] border-l-amber-400" : ""} ${dragging ? "opacity-40" : ""}`}>
      <div className="flex items-start gap-1">
        <b className="flex-1 min-w-0 text-[14.5px] font-bold leading-snug">{p.name}</b>
        {canEdit && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button type="button" onClick={(e) => e.stopPropagation()} aria-label={`Akce a přesun: ${p.name}`} data-testid={`board-menu-${p.id}`}
                className="-mr-1 -mt-1 w-8 h-8 rounded-full grid place-items-center text-muted-foreground hover:bg-secondary hover:text-foreground shrink-0">
                <MoreHorizontal size={17} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56" onClick={(e) => e.stopPropagation()}>
              <DropdownMenuItem onSelect={() => onOpen(p)}>Otevřít zakázku</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-xs font-semibold text-muted-foreground">Přesunout do fáze</DropdownMenuLabel>
              {BOARD_STAGES.map((s) => (
                <DropdownMenuItem key={s.key} disabled={stageOf(p.status).key === s.key} onSelect={() => onMove(p, s)} data-testid={`board-move-${p.id}-${s.label}`}>
                  {s.label}{stageOf(p.status).key === s.key && <Check size={15} className="ml-auto" />}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
      <span className="text-[12.5px] text-muted-foreground truncate">{p.investor?.name || p.address || "—"}</span>
      <div className="flex items-center justify-between gap-2 mt-1.5 min-h-[24px]">
        {p.contract_price != null
          ? <b className="text-[14.5px] font-bold tabular-nums whitespace-nowrap" title={formatCZK(p.contract_price)}>{p.contract_price ? formatCZKCompact(p.contract_price) : "—"}</b>
          : <span />}
        {p.site_manager_name && (
          <span className="flex items-center gap-1 shrink-0" title={`Stavbyvedoucí: ${p.site_manager_name}`}>
            <Avatar name={p.site_manager_name} size={24} className="bg-brand/15 text-brand text-[10px] font-bold" />
            {team > 1 && <small className="text-[11.5px] font-semibold text-muted-foreground">+{team - 1}</small>}
          </span>
        )}
      </div>
      <NextStep p={p} step={step} />
    </article>
  );
}

export function ProjectBoard({ projects, steps, canEdit, mobile, onOpen, onMove, onAdd }) {
  const [dragId, setDragId] = useState(null);
  const [over, setOver] = useState(null);
  const money = projects.some((p) => p.contract_price != null);
  const drop = (e, s) => {
    e.preventDefault(); setOver(null);
    const p = projects.find((x) => x.id === (e.dataTransfer.getData("text/plain") || dragId));
    setDragId(null);
    if (p && stageOf(p.status).key !== s.key) onMove(p, s);
  };
  return (
    // telefon: sloupce vedle sebe, posun prstem po sloupcích; PC: pět sloupců vedle sebe
    <div className="-mx-4 px-4 md:mx-0 md:px-0 grid items-start md:items-stretch grid-flow-col auto-cols-[82%] sm:auto-cols-[minmax(240px,1fr)] lg:auto-cols-[minmax(200px,1fr)] gap-3 overflow-x-auto snap-x snap-mandatory md:snap-none pb-2 scroll-px-4"
      data-testid="project-board">
      {BOARD_STAGES.map((s, i) => {
        const cards = projects.filter((p) => stageOf(p.status).key === s.key);
        const sum = cards.reduce((a, p) => a + (Number(p.contract_price) || 0), 0);
        return (
          <section key={s.key} data-testid={`board-col-${s.label}`} aria-label={`${s.label}, ${cards.length}`}
            onDragOver={(e) => { if (canEdit && e.dataTransfer.types.includes("text/plain")) { e.preventDefault(); e.dataTransfer.dropEffect = "move"; if (over !== s.key) setOver(s.key); } }}
            onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setOver(null); }}
            onDrop={(e) => drop(e, s)}
            className={`snap-start flex flex-col min-w-0 rounded-[20px] p-2 bg-card/45 dark:bg-card/30 border border-border/60 backdrop-blur-sm transition-shadow
              ${over === s.key ? "ring-2 ring-brand/60" : ""}`}>
            <header className="bg-card rounded-[14px] px-3 pt-2.5 pb-3 shadow-[0_1px_2px_rgba(20,24,33,.05)]">
              <b className="text-[15px] font-bold">{s.label}</b>
              <div className="text-[12.5px] text-muted-foreground tabular-nums" title={money ? formatCZK(sum) : undefined}>
                {money ? `${formatCZKCompact(sum)} · ` : ""}{cards.length}
              </div>
              <div className="mt-2 h-1 rounded-full bg-secondary overflow-hidden" aria-hidden="true">
                <i className={`block h-full rounded-full ${i === BOARD_STAGES.length - 1 ? "bg-success" : "bg-brand"}`} style={{ width: `${((i + 1) / BOARD_STAGES.length) * 100}%` }} />
              </div>
            </header>
            <div className="flex flex-col gap-2 mt-2 flex-1">
              {cards.map((p) => (
                <Card key={p.id} p={p} step={steps ? steps[p.id] || { next: null, idle_days: null } : undefined} canEdit={canEdit} drag={canEdit && !mobile}
                  dragging={dragId === p.id} onOpen={onOpen} onMove={onMove}
                  onDragStart={(e, x) => { e.dataTransfer.setData("text/plain", x.id); e.dataTransfer.effectAllowed = "move"; setDragId(x.id); }}
                  onDragEnd={() => { setDragId(null); setOver(null); }} />
              ))}
              {!cards.length && <p className="py-6 text-center text-[13px] text-muted-foreground">Tady nic není</p>}
            </div>
            {canEdit && (
              <button type="button" onClick={() => onAdd(s)} data-testid={`board-add-${s.label}`}
                className="mt-2 h-10 px-3 rounded-[12px] text-[13.5px] font-semibold text-muted-foreground hover:bg-card hover:text-foreground inline-flex items-center gap-1.5">
                <Plus size={16} />Přidat
              </button>
            )}
          </section>
        );
      })}
    </div>
  );
}
